import { PayloadAction } from '@reduxjs/toolkit';
import { createNumberContactDictionary } from 'app/data/webphone/webphoneSlice';
import { defaultsDeep } from 'lodash';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { localSaga } from './saga';
import { LocalState } from './types';

const persistedInitialState = {
  help_mode: false,
};

export const initialState: LocalState = {
  ...persistedInitialState,
  pressed: false,
  sidebar_key: '',
  sidebar_mini: null, // uses default, if not forced
  nav_breadcrumbs: [],
  search_in_page_focus: false,
  search_in_page_options: [],
  search_in_page_options_currently_shown_in_list: [], // the output/matches of the filter
  search_in_page_option_highlighted: null, // the currently-highlighted value
  poke_holes: [],
  webphone_visible: false,
  webphone_background: false,
  channels: [],
  // this is used for disabling a list query until all mutations
  // have been carried out to prevent invalidation causing issues
  queries_enabled: true,
};

export const persistMask = Object.keys(persistedInitialState)
  .join(',')
  .replace(/\s/g, '');

const slice = createSlice({
  name: 'local',
  initialState: () => {
    // eh, merging the "saved" state with the "initial"/"default" state is hard, so simplify by only providing top-level keys that can be saved
    // - doing deep-merge is error-prone and more complicated
    let state = initialState;

    try {
      state = defaultsDeep(
        {
          // @ts-ignores
          ...(JSON.parse(localStorage.getItem(`app_persist_state_local`)) ??
            {}),
        },
        initialState,
      );
      // state = {
      //   ...initialState,
      //   // @ts-ignore
      //   ...(JSON.parse(localStorage.getItem(`app_persist_state_webphone`)) ??
      //     initialState),
      // };
    } catch (err) {}

    return state;
  },
  reducers: {
    someAction(state, action: PayloadAction<any>) {
      state.pressed = !state.pressed;
    },
    set_sidebar_key(state, action: PayloadAction<any>) {
      state.sidebar_key = action.payload;
    },
    set_sidebar_mini(state, action: PayloadAction<any>) {
      state.sidebar_mini = action.payload;
    },
    set_nav_breadcrumbs(state, action: PayloadAction<any>) {
      state.nav_breadcrumbs = [action.payload, ...state.nav_breadcrumbs];
    },
    unregister_nav_breadcrumbs(state, action: PayloadAction<any>) {
      state.nav_breadcrumbs = state.nav_breadcrumbs.filter(
        v => !action.payload,
      );
    },
    set_search_in_page_option_highlighted(state, action: PayloadAction<any>) {
      state.search_in_page_option_highlighted = action.payload;
    },
    add_search_in_page_options(state, action: PayloadAction<any>) {
      state.search_in_page_options = state.search_in_page_options.concat(
        action.payload,
      );
    },
    remove_search_in_page_options(state, action: PayloadAction<any>) {
      state.search_in_page_options = state.search_in_page_options.filter(
        opt => opt.id !== action.payload,
      );
    },
    add_poke_holes(state, action: PayloadAction<any>) {
      state.poke_holes = state.poke_holes.concat(action.payload);
    },
    remove_poke_holes(state, action: PayloadAction<any>) {
      state.poke_holes = state.poke_holes.filter(
        opt => opt.id !== action.payload,
      );
    },
    set_search_in_page_focus(state, action: PayloadAction<any>) {
      state.search_in_page_focus = action.payload;
    },
    set_search_in_page_options_currently_shown_in_list(
      state,
      action: PayloadAction<any>,
    ) {
      state.search_in_page_options_currently_shown_in_list = action.payload;
    },
    set_webphone_visible(state, action: PayloadAction<any>) {
      state.webphone_visible = action.payload;
    },
    set_webphone_background(state, action: PayloadAction<any>) {
      state.webphone_background = action.payload;
    },
    set_channels(state, action: PayloadAction<any>) {
      state.channels = action.payload;
    },
    queries_enabled(state, action: PayloadAction<boolean>) {
      state.queries_enabled = action.payload;
    },
    set_help_mode(state, action: PayloadAction<boolean>) {
      state.help_mode = action.payload;
    },
  },
  extraReducers: {
    'auth/logout': (state, action) => {
      return {
        ...initialState,
      };
    },
  },
});

export default slice.reducer;

export const { actions, reducer } = slice;

export const useLocalSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: localSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useLocalSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
